// API service for HDOT Airport Checkpoint Management Application
import {
  Terminal, Checkpoint, Lane, LaneType, OperatingHours,
  Section, SectionUnit,
  StatusChangeLog, SystemSnapshot, DashboardStats,
  SyncResponse, BulkSyncResponse, APIResponse,
  TerminalFormData, CheckpointFormData, CheckpointUpdateData,
  LaneFormData, LaneUpdateData, LaneTypeFormData,
  SectionFormData, SectionUnitFormData,
  OperatingHoursFormData, CascadeResponse,
  User
} from '../types';
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, HTTP_STATUS, ERROR_MESSAGES } from '../config/constants';
import { msalInstance } from '../contexts/AuthContext';


// header function to get user name and details

const getUpdatedByFromUser = (user?: User | null): string => {
  // Try MSAL first (same as Header)
  if (typeof window !== 'undefined' && (window as any).msalInstance) {
    try {
      const accounts = (window as any).msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const account = accounts[0];
        if (account.name) {
          return account.name; // "John Doe" - BEST for audit trails
        } else if (account.username) {
          return account.username; // "john.doe@company.com"
        }
      }
    } catch (error) {
      // Silently continue to fallbacks if MSAL access fails
    }
  }
  
  // Fallback to User object (same as Header)
  if (user?.fullName) return user.fullName;
  //if (user?.name) return user.name;
  if (user?.username) return user.username;
  
  return 'unknown_user';
};

// API service class with authentication support
class ApiService {
  
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }
  

  // Private method to make authenticated HTTP requests
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const baseUrl = this.getBaseURL();
      
      // Fix double slash issue - simple URL construction
      let url = baseUrl + endpoint;
      url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
      url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
      
      // Making HTTP request
      
      // Get access token if available
      // Get access token if available
      let token: string | null = null;
      try {
        const { getCurrentConfig } = await import('../config');
        const config = getCurrentConfig();
        
        if (config.auth.enableAuthentication) {
          const { msalInstance } = await import('../contexts/AuthContext');
          const accounts = msalInstance.getAllAccounts();
          
          if (accounts.length > 0) {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: [getApiScope()], // Use scope from .env
              account: accounts[0]
            });
            token = tokenResponse.accessToken;
          }
        }
      } catch (error) {
        // Silent fail for token acquisition
      }
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      
      // Add any additional headers from options
      if (options?.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }
      
      // Add authorization header if token is available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
              // Request prepared
      
      const response = await fetch(url, {
        headers,
        ...options,
      });
      
              // Response received

      if (!response.ok) {
                  // HTTP error occurred
        // Handle different HTTP status codes
        switch (response.status) {
          case HTTP_STATUS.NOT_FOUND:
            throw new Error(ERROR_MESSAGES.NOT_FOUND);
          case HTTP_STATUS.BAD_REQUEST:
            throw new Error(ERROR_MESSAGES.VALIDATION_ERROR);
          case HTTP_STATUS.INTERNAL_SERVER_ERROR:
            throw new Error(ERROR_MESSAGES.SERVER_ERROR);
          case 401:
            throw new Error('Authentication required - please log in again');
          case 403:
            throw new Error('Access denied - insufficient permissions');
          default:
            throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const responseData = await response.json();
              // Response data processed
      return responseData;
    } catch (error) {
      // Handle network errors and other exceptions
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }
      throw error;
    }
  }

  // Health and System
  async getHealth(): Promise<APIResponse> {
    return this.request<APIResponse>(API_ENDPOINTS.HEALTH);
  }

  async getDetailedHealth(): Promise<APIResponse> {
    return this.request<APIResponse>(API_ENDPOINTS.HEALTH_DETAILED);
  }

  async getDatabaseHealth(): Promise<APIResponse> {
    return this.request<APIResponse>(API_ENDPOINTS.HEALTH_DATABASE);
  }

  async getApiInfo(): Promise<APIResponse> {
    return this.request<APIResponse>(API_ENDPOINTS.ROOT);
  }

  async getDashboard(): Promise<DashboardStats> {
    return this.request<DashboardStats>(API_ENDPOINTS.DASHBOARD);
  }

  async getDashboardWithHash(hash: string): Promise<any> {
    return this.request<any>(API_ENDPOINTS.DASHBOARD_WITH_HASH(hash));
  }

  async getDashboardHashOnly(): Promise<{ hash: string }> {
    return this.request<{ hash: string }>(API_ENDPOINTS.DASHBOARD_HASH_ONLY);
  }

  // Terminals
  async getTerminals(): Promise<Terminal[]> {
    return this.request<Terminal[]>(API_ENDPOINTS.TERMINALS);
  }

  async getTerminal(id: number): Promise<Terminal> {
    return this.request<Terminal>(API_ENDPOINTS.TERMINAL_BY_ID(id));
  }

  async createTerminal(data: TerminalFormData): Promise<Terminal> {
    return this.request<Terminal>(API_ENDPOINTS.TERMINALS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTerminal(id: number, data: Partial<TerminalFormData>): Promise<Terminal> {
    return this.request<Terminal>(API_ENDPOINTS.TERMINAL_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTerminal(id: number): Promise<APIResponse> {
    return this.request<APIResponse>(API_ENDPOINTS.TERMINAL_BY_ID(id), { method: 'DELETE' });
  }

  // Sections
  async getSections(terminalId?: number): Promise<Section[]> {
    const url = terminalId ? API_ENDPOINTS.SECTIONS_BY_CHECKPOINT(terminalId) : API_ENDPOINTS.SECTIONS;
    const response = await this.request<{ data: { sections: Section[] } }>(url);
    return response.data.sections || [];
  }

  async getSection(id: number): Promise<Section> {
    return this.request<Section>(API_ENDPOINTS.SECTION_BY_ID(id));
  }

  async createSection(data: SectionFormData): Promise<Section> {
    return this.request<Section>(API_ENDPOINTS.SECTIONS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSection(id: number, data: Partial<SectionFormData>): Promise<Section> {
    return this.request<Section>(API_ENDPOINTS.SECTION_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSection(id: number): Promise<APIResponse> {
    return this.request<APIResponse>(API_ENDPOINTS.SECTION_BY_ID(id), { method: 'DELETE' });
  }

  // Checkpoints
  async getCheckpoints(terminalId?: number): Promise<Checkpoint[]> {
    const url = terminalId ? API_ENDPOINTS.CHECKPOINTS_BY_TERMINAL(terminalId) : API_ENDPOINTS.CHECKPOINTS;
    return this.request<Checkpoint[]>(url);
  }

  async getCheckpoint(id: number): Promise<Checkpoint> {
    return this.request<Checkpoint>(API_ENDPOINTS.CHECKPOINT_BY_ID(id));
  }

  async createCheckpoint(data: CheckpointFormData): Promise<Checkpoint> {
    return this.request<Checkpoint>(API_ENDPOINTS.CHECKPOINTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCheckpoint(id: number, data: CheckpointUpdateData): Promise<Checkpoint> {
    return this.request<Checkpoint>(API_ENDPOINTS.CHECKPOINT_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateCheckpointStatus(id: number, statusData: {
    is_open: boolean;
    is_exception?: boolean;
    exception_end_time?: string | null;
    updated_by: string;
    reason?: string;
  }): Promise<{ message: string }> {
    return this.request<{ message: string }>(API_ENDPOINTS.CHECKPOINT_STATUS(id), {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  }

  // Checkpoint Toggle Methods (from Postman collection)
  async toggleCheckpoint(id: number, user?: User | null): Promise<{ message: string }> {
    // Use the cascade endpoint with proper parameters
    const params = new URLSearchParams();
    params.append('cascade', 'true');
    params.append('updated_by', getUpdatedByFromUser(user));
    
    const endpoint = `${API_ENDPOINTS.CHECKPOINT_TOGGLE(id)}?${params.toString()}`;
          // Making toggle request
    
    return this.request<{ message: string }>(endpoint, {
      method: 'PUT',
    });
  }

  async toggleCheckpointWithCascade(id: number, cascade: boolean, updatedBy?: string): Promise<CascadeResponse> {
    return this.request<CascadeResponse>(API_ENDPOINTS.CHECKPOINT_TOGGLE_WITH_CASCADE(id, cascade, updatedBy), {
      method: 'PUT',
    });
  }

  // Lanes
  async getLanes(checkpointId?: number): Promise<Lane[]> {
    const url = checkpointId ? `/lanes?checkpoint_id=${checkpointId}` : '/lanes';
    return this.request<Lane[]>(url);
  }

  async getLane(id: number): Promise<Lane> {
    return this.request<Lane>(`/lanes/${id}`);
  }

  async createLane(data: LaneFormData): Promise<Lane> {
    return this.request<Lane>('/lanes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLane(id: number, data: LaneUpdateData): Promise<Lane> {
    return this.request<Lane>(`/lanes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // // Lane Assignment Methods
  // async toggleLaneAssignment(
  //   id: number, 
  //   cascade = true, 
  //   updatedBy = 'api_user', 
  //   reason?: string
  // ): Promise<{ message: string }> {
  //   const params = new URLSearchParams();
  //   params.append('cascade', cascade.toString());
  //   params.append('updated_by', updatedBy);
  //   if (reason) params.append('reason', reason);

  //   return this.request<{ message: string }>(
  //     `/laneassignments/${id}/toggle?${params.toString()}`, 
  //     { method: 'PUT' }
  //   );
  // }

  // Lane Assignment Methods
async toggleLaneAssignment(
  id: number, 
  cascade = true, 
  user?: User | null,      // ✅ CHANGED: Replace updatedBy with user parameter
  reason?: string
): Promise<{ message: string }> {
  const params = new URLSearchParams();
  params.append('cascade', cascade.toString());
  params.append('updated_by', getUpdatedByFromUser(user)); // ✅ CHANGED: Use the helper function
  if (reason) params.append('reason', reason);

  return this.request<{ message: string }>(
    `/laneassignments/${id}/toggle?${params.toString()}`, 
    { method: 'PUT' }
  );
}

  async changeLaneAssignmentType(
    laneAssignmentId: number, 
    newLaneTypeId: number, 
    reason?: string
  ): Promise<any> {
    const data = {
      new_lanetypeID: newLaneTypeId,
      cascade: true, // Always true as per requirement
      reason: reason || 'Frontend lane type change'
    };
    
    return this.request<any>(`/laneassignments/${laneAssignmentId}/change-assignment`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Status and Monitoring
  async getStatusChanges(entityType?: string, entityId?: number, limit = 100): Promise<StatusChangeLog[]> {
    let url = `/status-changes?limit=${limit}`;
    if (entityType) url += `&entity_type=${entityType}`;
    if (entityId) url += `&entity_id=${entityId}`;
    return this.request<StatusChangeLog[]>(url);
  }

  async getSystemSnapshots(logId?: number, sectionUnitId?: number, limit = 100): Promise<SystemSnapshot[]> {
    let url = `/system-snapshots?limit=${limit}`;
    if (logId) url += `&log_id=${logId}`;
    if (sectionUnitId) url += `&section_unit_id=${sectionUnitId}`;
    return this.request<SystemSnapshot[]>(url);
  }

  // Operating Hours - Checkpoints
  async getCheckpointOperatingHours(checkpointId: number): Promise<OperatingHours[]> {
    return this.request<OperatingHours[]>(`/operating-hours/checkpoints/${checkpointId}`);
  }

  async createCheckpointOperatingHours(checkpointId: number, data: OperatingHoursFormData): Promise<OperatingHours> {
    return this.request<OperatingHours>(`/operating-hours/checkpoints/${checkpointId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCheckpointOperatingHours(checkpointId: number, hoursId: number, data: Partial<OperatingHoursFormData>): Promise<OperatingHours> {
    return this.request<OperatingHours>(`/operating-hours/checkpoints/${checkpointId}/${hoursId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCheckpointOperatingHours(checkpointId: number, hoursId: number): Promise<APIResponse> {
    return this.request<APIResponse>(`/operating-hours/checkpoints/${checkpointId}/${hoursId}`, {
      method: 'DELETE',
    });
  }

  // Operating Hours - Lanes
  async getLaneOperatingHours(laneId: number): Promise<OperatingHours[]> {
    return this.request<OperatingHours[]>(`/operating-hours/lanes/${laneId}`);
  }

  async createLaneOperatingHours(laneId: number, data: OperatingHoursFormData): Promise<OperatingHours> {
    return this.request<OperatingHours>(`/operating-hours/lanes/${laneId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLaneOperatingHours(laneId: number, hoursId: number, data: Partial<OperatingHoursFormData>): Promise<OperatingHours> {
    return this.request<OperatingHours>(`/operating-hours/lanes/${laneId}/${hoursId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Lane Types
  async getLaneTypes(checkpointId?: number): Promise<LaneType[]> {
    // Get all lane types (backend checkpoint_id filter seems to not work properly)
    const url = '/lanetypes/';
    const response = await this.request<{ success: boolean; data: { lane_types: LaneType[] } }>(url);
    const allLaneTypes = response.data.lane_types || [];
    
    // Filter by checkpoint_id if provided (frontend filtering)
    if (checkpointId) {
      return allLaneTypes.filter(laneType => laneType.checkpoint_id === checkpointId);
    }
    
    return allLaneTypes;
  }

  async createLaneType(data: LaneTypeFormData): Promise<LaneType> {
    const response = await this.request<{ data: LaneType }>('/lanetypes/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateLaneType(id: number, data: Partial<LaneTypeFormData>): Promise<LaneType> {
    const response = await this.request<{ data: LaneType }>(`/lanetypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Section Units
  async getSectionUnits(sectionId?: number): Promise<SectionUnit[]> {
    const url = sectionId ? `/section-units?section_id=${sectionId}` : '/section-units';
    return this.request<SectionUnit[]>(url);
  }

  async getSectionUnit(id: number): Promise<SectionUnit> {
    return this.request<SectionUnit>(`/section-units/${id}`);
  }

  async createSectionUnit(data: SectionUnitFormData): Promise<SectionUnit> {
    return this.request<SectionUnit>('/section-units', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSectionUnit(id: number, data: Partial<SectionUnitFormData>): Promise<SectionUnit> {
    return this.request<SectionUnit>(`/section-units/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSectionUnit(id: number): Promise<APIResponse> {
    return this.request<APIResponse>(`/section-units/${id}`, {
      method: 'DELETE',
    });
  }

  // Sync Operations
  async syncCheckpoint(checkpointId: number): Promise<SyncResponse> {
    return this.request<SyncResponse>(`/sync/checkpoint/${checkpointId}`, {
      method: 'POST',
    });
  }

  async syncLane(laneId: number): Promise<SyncResponse> {
    return this.request<SyncResponse>(`/sync/lane/${laneId}`, {
      method: 'POST',
    });
  }

  async syncAllCheckpoints(): Promise<BulkSyncResponse> {
    return this.request<BulkSyncResponse>('/sync/all-checkpoints', {
      method: 'POST',
    });
  }

  async syncAllLanes(): Promise<BulkSyncResponse> {
    return this.request<BulkSyncResponse>('/sync/all-lanes', {
      method: 'POST',
    });
  }

  async syncEverything(): Promise<any> {
    return this.request<any>('/sync/everything', {
      method: 'POST',
    });
  }

  // System Configuration
  async getSystemConfig(): Promise<Record<string, any>[]> {
    return this.request<Record<string, any>[]>('/config');
  }

  async updateSystemConfig(configKey: string, configValue: string): Promise<Record<string, any>> {
    return this.request<Record<string, any>>(`/config/${configKey}`, {
      method: 'PUT',
      body: JSON.stringify(configValue),
    });
  }

  // Asset Methods
  async getAssets(): Promise<any> {
    return this.request<any>(API_ENDPOINTS.ASSETS);
  }

  // async toggleAsset(
  //   id: number, 
  //   cascade = true, 
  //   updatedBy = 'api_user', 
  //   reason?: string
  // ): Promise<{ message: string }> {
  //   const params = new URLSearchParams();
  //   params.append('cascade', cascade.toString());
  //   params.append('updated_by', updatedBy);
  //   if (reason) params.append('reason', reason);

  //   return this.request<{ message: string }>(
  //     `${API_ENDPOINTS.ASSET_TOGGLE(id)}?${params.toString()}`, 
  //     { method: 'PUT' }
  //   );
  // }

  async toggleAsset(
  id: number, 
  cascade = true, 
  user?: User | null,      // ✅ CHANGED: Replace updatedBy with user parameter
  reason?: string
): Promise<{ message: string }> {
  const params = new URLSearchParams();
  params.append('cascade', cascade.toString());
  params.append('updated_by', getUpdatedByFromUser(user)); // ✅ CHANGED: Use the helper function
  if (reason) params.append('reason', reason);

  return this.request<{ message: string }>(
    `${API_ENDPOINTS.ASSET_TOGGLE(id)}?${params.toString()}`, 
    { method: 'PUT' }
  );
}
  // Helper method to get current configuration
  getConfig() {
    const currentConfig = getCurrentConfig();
    return {
      baseURL: currentConfig.apiBaseUrl,
      environment: currentConfig.environment,
    };
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;